import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Snackbar, Alert, Chip, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useLayout } from "../../context/LayoutContext";
import { NotificationPositions } from "../../context/NotificationContext";

import { useQuery } from "@tanstack/react-query";
import { useOperationService } from "../../service/documentationVehicleService";
import SchedulerHeader from "../layout/SchedulerHeader";
import MonthSelector from "./MonthSelector";
import WeekCalendar from "./WeekCalendar";
import LoadingAnimation from "../loading/LoadingAnimation";
import VehicleDrawer from "../../components/ui/drawner/VehicleDrawer";
import EventsDrawer from "../../components/ui/drawner/EventsDrawer";
import CapacityDrawer from "../../components/ui/drawner/CapacityDrawer";
import RouteDrawer from "../../components/ui/drawner/RouteDrawer";
import CancelVehicleModal from "../ui/modals/CancelVehicleModal";
import { useSchedule } from "../../hooks/vehicle/useSchedule";
import AddVehicleModal from "../ui/modals/AddVehicleModal";
import "../../assets/scss/views/SchedulerCalendar.scss";

const SchedulerCalendar = ({ operationId }) => {
  const { theme, selectedDate, handleDateChange } = useLayout();

  // NUEVO: Estado para rastrear el mes actual de forma más precisa
  const currentMonthYear = useMemo(() => {
    const date = selectedDate ? new Date(selectedDate) : new Date();
    if (isNaN(date.getTime())) {
      const now = new Date();
      return { month: now.getMonth(), year: now.getFullYear() };
    }
    return { month: date.getMonth(), year: date.getFullYear() };
  }, [selectedDate]);

  //Asegurar sincronización correcta con selectedDate
  const [localMonth, setLocalMonth] = useState(() => {
    const date = selectedDate ? new Date(selectedDate) : new Date();
    return date.getMonth();
  });

  // Simplificación del estado - usar solo selectedDate como fuente de verdad
  const currentDate = useMemo(() => {
    if (!selectedDate) return new Date();
    const date = new Date(selectedDate);
    return isNaN(date.getTime()) ? new Date() : date;
  }, [selectedDate]);

  const [weeksData, setWeeksData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPredictions, setShowPredictions] = useState(true);

  // Estados para los drawers
  const [vehicleDrawerOpen, setVehicleDrawerOpen] = useState(false);
  const [eventsDrawerOpen, setEventsDrawerOpen] = useState(false);
  const [capacityDrawerOpen, setCapacityDrawerOpen] = useState(false);
  const [routeDrawerOpen, setRouteDrawerOpen] = useState(false);

  // Estados para rutas
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [routes, setRoutes] = useState([]);

  // Estado para modal de cancelación
  const [cancelModal, setCancelModal] = useState({
    open: false,
    scheduleId: null,
    vehicleInfo: null,
  });

  // Estado para modal de añadir vehículo
  const [addVehicleModal, setAddVehicleModal] = useState({
    open: false,
    date: null,
  });

  // Estado para alertas
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const { id: routeOperationId } = useParams();
  const currentOperationId = useMemo(
    () => operationId || routeOperationId,
    [operationId, routeOperationId]
  );

  const operationService = useOperationService();

  const {
    useScheduleByDateRange,
    useCapacityStatus,
    useOperationRoutes,
    useDefaultRoute,
    addVehicle,
    reorderVehicles,
    moveVehicle,
    removeVehicle,
    cancelVehicle,
    isLoading: isScheduleLoading,
  } = useSchedule(currentOperationId, selectedRouteId);

  const { data: operationInfo, isLoading: isLoadingOperation } = useQuery({
    queryKey: ["operation", currentOperationId],
    queryFn: () => operationService.getOperationById(currentOperationId),
    staleTime: 30 * 60 * 1000,
    enabled: !!currentOperationId,
    select: (response) => response.data,
  });

  const { data: routesData, isLoading: isLoadingRoutes } = useOperationRoutes();

  const { data: defaultRouteData } = useDefaultRoute({
    enabled: !!currentOperationId && !selectedRouteId,
  });

  const showNotification = useCallback((notification) => {
    setSnackbar({
      open: true,
      message: notification.message,
      severity: notification.severity || "info",
    });
  }, []);

  useEffect(() => {
    if (routesData?.data) {
      setRoutes(routesData.data.filter((r) => r.is_active));

      if (!selectedRouteId && defaultRouteData?.data) {
        setSelectedRouteId(defaultRouteData.data.id);
      }
    }
  }, [routesData, defaultRouteData, selectedRouteId]);

  const getMonthDateRange = useCallback((date) => {
    if (!date) return { startDate: null, endDate: null };

    const targetDate = date instanceof Date ? date : new Date(date);

    if (isNaN(targetDate.getTime())) {
      console.error("Fecha inválida:", date);
      return { startDate: null, endDate: null };
    }

    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    return {
      startDate: firstDay.toISOString().split("T")[0],
      endDate: lastDay.toISOString().split("T")[0],
    };
  }, []);

  // ACTUALIZADO: Simplificar el cálculo del rango de fechas
  const dateRange = useMemo(() => {
    return getMonthDateRange(currentDate);
  }, [currentDate, getMonthDateRange]);

const isPastDate = useCallback((dateStr) => {
  if (!dateStr) return false;
  
  // Crear fechas usando solo la parte de fecha sin tiempo para evitar problemas de zona horaria
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  
  // Normalizar la fecha de entrada
  const inputDateStr = typeof dateStr === 'string' ? dateStr.split('T')[0] : dateStr;
  
  // Comparación directa de strings de fecha (YYYY-MM-DD)
  const result = inputDateStr < todayStr;
  
  return result;
}, []);

const isToday = useCallback((dateStr) => {
  if (!dateStr) return false;
  
  // Crear fecha de hoy como string
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  
  // Normalizar la fecha de entrada
  const inputDateStr = typeof dateStr === 'string' ? dateStr.split('T')[0] : dateStr;
  
  // Comparación directa de strings
  const result = inputDateStr === todayStr;
  
  
  return result;
}, []);

  const {
    data: scheduleData,
    isLoading: isLoadingSchedule,
    error: scheduleError,
    refetch: refetchSchedule,
  } = useScheduleByDateRange(dateRange.startDate, dateRange.endDate, {
    enabled:
      !!currentOperationId && !!dateRange.startDate && !!dateRange.endDate,
    refetchOnWindowFocus: false,
    includeCapacity: true,
  });

  const { data: capacityStatus, isLoading: isLoadingCapacity } =
    useCapacityStatus(dateRange.startDate, dateRange.endDate, {
      enabled:
        !!currentOperationId && !!dateRange.startDate && !!dateRange.endDate,
    });

  // Algoritmo de predicción con patrón de rotación y filtrado por ruta
  const generateSimplePredictions = useCallback(
    (scheduledDays, weeksData, capacityInfo) => {
      const vehiclePattern = [];
      const scheduledVehiclesByDay = new Map();
      const eventsMap = new Map(); // Mapa para rastrear eventos por fecha

      // Primera pasada: recopilar vehículos programados y eventos
      Object.entries(scheduledDays).forEach(([dateStr, dayData]) => {
        // Registrar eventos
        if (dayData.events && dayData.events.length > 0) {
          eventsMap.set(dateStr, dayData.events);
        }

        if (dayData.vehicles && dayData.vehicles.length > 0) {
          let filteredVehicles = dayData.vehicles.filter(
            (v) => !v.isPredicted && !v.is_cancelled
          );

          if (selectedRouteId) {
            filteredVehicles = filteredVehicles.filter(
              (v) =>
                v.operationRouteId === selectedRouteId ||
                v.route?.id === selectedRouteId
            );
          } else {
            filteredVehicles = filteredVehicles.filter(
              (v) => !v.operationRouteId
            );
          }

          const manualVehicles = filteredVehicles.sort(
            (a, b) => a.position - b.position
          );

          scheduledVehiclesByDay.set(dateStr, manualVehicles);

          manualVehicles.forEach((vehicle) => {
            if (
              !vehiclePattern.find((v) => v.vehicleId === vehicle.vehicleId)
            ) {
              vehiclePattern.push({
                vehicleId: vehicle.vehicleId,
                plate: vehicle.plate,
                operation: vehicle.operation,
                color: vehicle.color,
                operationRouteId: selectedRouteId,
              });
            }
          });
        }
      });

      if (vehiclePattern.length === 0) {
        showNotification({
          message: "No hay patrón establecido para la ruta actual",
          severity: "warning",
          position: NotificationPositions.BOTTOM_LEFT,
          autoHideDuration: 4000,
        });
        return weeksData;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let patternIndex = 0;
      let deferredVehicles = []; // Vehículos diferidos por eventos

      return weeksData.map((week) => ({
        ...week,
        days: week.days.map((day) => {
          if (!day.date) return day;

          const dateStr =
            typeof day.date === "string"
              ? day.date
              : day.date.toISOString().split("T")[0];
          const dateObj = new Date(dateStr);
          dateObj.setHours(0, 0, 0, 0);

          // Verificar si hay eventos en este día
          const hasEvents = eventsMap.has(dateStr);
          const events = eventsMap.get(dateStr) || [];

          // Si es un día futuro sin programación manual
          if (dateObj >= today && !scheduledVehiclesByDay.has(dateStr)) {
            const routeCapacityInfo = capacityInfo[dateStr];
            const dayCapacity =
              routeCapacityInfo?.plannedCapacity || vehiclePattern.length;

            const predictedVehicles = [];
            const usedVehicles = new Set();
            let conflicts = [];

            // Si hay eventos, marcar el día como bloqueado
            if (hasEvents) {
              // Añadir vehículos diferidos al siguiente día sin eventos
              deferredVehicles.push(
                ...Array(dayCapacity)
                  .fill(null)
                  .map((_, idx) => ({
                    vehicleData:
                      vehiclePattern[
                        (patternIndex + idx) % vehiclePattern.length
                      ],
                    originalDate: dateStr,
                  }))
              );

              patternIndex += dayCapacity;

              const existingData = scheduledDays[dateStr] || {
                vehicles: [],
                events: [],
              };

              return {
                ...day,
                vehicles: [],
                events: events,
                capacity: routeCapacityInfo || {},
                hasCapacityIssue: false,
                isBlockedByEvent: true,
                blockedMessage: `Día bloqueado por evento: ${
                  events[0]?.title || "Evento"
                }`,
                // Actualizar el contador para mostrar 0/capacidad cuando hay eventos
                capacity: {
                  ...routeCapacityInfo,
                  scheduledCount: 0,
                  scheduled: 0,
                  status: "blocked_by_event",
                },
              };
            }

            // Procesar vehículos diferidos primero
            while (
              deferredVehicles.length > 0 &&
              predictedVehicles.length < dayCapacity
            ) {
              const deferred = deferredVehicles.shift();
              const vehicle = deferred.vehicleData;

              if (!usedVehicles.has(vehicle.vehicleId)) {
                predictedVehicles.push({
                  id: `pred-${dateStr}-${predictedVehicles.length}-${vehicle.vehicleId}`,
                  vehicleId: vehicle.vehicleId,
                  plate: vehicle.plate,
                  operation: vehicle.operation,
                  color: vehicle.color,
                  position: predictedVehicles.length,
                  isPredicted: true,
                  reason: `Diferido desde ${deferred.originalDate}`,
                  operationRouteId: selectedRouteId,
                  isDeferredFromEvent: true,
                });
                usedVehicles.add(vehicle.vehicleId);
              }
            }

            // Continuar con el patrón normal
            for (
              let i = predictedVehicles.length;
              i < dayCapacity && i < vehiclePattern.length * 2;
              i++
            ) {
              const vehicle =
                vehiclePattern[patternIndex % vehiclePattern.length];

              if (usedVehicles.has(vehicle.vehicleId)) {
                conflicts.push(
                  `Conflicto: ${vehicle.plate} ya asignado en este día`
                );
                patternIndex++;
                continue;
              }

              const isAvailable =
                !scheduledVehiclesByDay.has(dateStr) ||
                !scheduledVehiclesByDay
                  .get(dateStr)
                  .find((v) => v.vehicleId === vehicle.vehicleId);

              if (isAvailable) {
                predictedVehicles.push({
                  id: `pred-${dateStr}-${i}-${vehicle.vehicleId}`,
                  vehicleId: vehicle.vehicleId,
                  plate: vehicle.plate,
                  operation: vehicle.operation,
                  color: vehicle.color,
                  position: i,
                  isPredicted: true,
                  reason: "Patrón de rotación",
                  operationRouteId: selectedRouteId,
                });

                usedVehicles.add(vehicle.vehicleId);
              } else {
                conflicts.push(`Conflicto: ${vehicle.plate} no disponible`);
              }

              patternIndex++;
            }

            const hasFaltantes = predictedVehicles.length < dayCapacity;

            const existingData = scheduledDays[dateStr] || {
              vehicles: [],
              events: [],
            };
            let existingVehicles = existingData.vehicles || [];

            if (selectedRouteId) {
              existingVehicles = existingVehicles.filter(
                (v) =>
                  v.operationRouteId === selectedRouteId ||
                  v.route?.id === selectedRouteId
              );
            } else {
              existingVehicles = existingVehicles.filter(
                (v) => !v.operationRouteId
              );
            }

            // Calcular capacidad correctamente incluyendo predicciones
            const totalVehicles = [...existingVehicles, ...predictedVehicles];
            const activeVehicles = totalVehicles.filter((v) => !v.is_cancelled);
            const scheduledCount = activeVehicles.length;
            const plannedCapacity = routeCapacityInfo?.plannedCapacity || 0;

            let status = "no_capacity_set";
            if (plannedCapacity > 0) {
              const difference = scheduledCount - plannedCapacity;
              if (difference > 0) {
                status = "over_capacity";
              } else if (difference < 0) {
                status = "under_capacity";
              } else {
                status = "at_capacity";
              }
            }

            const capacityData = {
              ...routeCapacityInfo,
              scheduled: scheduledCount,
              scheduledCount: scheduledCount,
              planned: plannedCapacity,
              plannedCapacity: plannedCapacity,
              status: status,
              difference: scheduledCount - plannedCapacity,
              isOverCapacity: status === "over_capacity",
              isUnderCapacity: status === "under_capacity",
            };

            return {
              ...day,
              vehicles: totalVehicles,
              events: existingData.events || [],
              capacity: capacityData,
              hasCapacityIssue:
                capacityData.status === "over_capacity" ||
                capacityData.status === "under_capacity",
              hasPredictionConflicts: conflicts.length > 0,
              predictionConflicts: conflicts,
              hasFaltantes: hasFaltantes,
              faltantesCount: hasFaltantes
                ? dayCapacity - predictedVehicles.length
                : 0,
              hasDeferredVehicles: predictedVehicles.some(
                (v) => v.isDeferredFromEvent
              ),
            };
          }

          // Para días con programación existente
          const existingData = scheduledDays[dateStr] || {
            vehicles: [],
            events: [],
          };
          let existingVehicles = existingData.vehicles || [];

          if (selectedRouteId) {
            existingVehicles = existingVehicles.filter(
              (v) =>
                v.operationRouteId === selectedRouteId ||
                v.route?.id === selectedRouteId
            );
          } else {
            existingVehicles = existingVehicles.filter(
              (v) => !v.operationRouteId
            );
          }

          // Si hay eventos, marcar como bloqueado
          if (hasEvents && existingVehicles.length > 0) {
            // Diferir vehículos programados
            existingVehicles.forEach((vehicle) => {
              if (!vehicle.is_cancelled) {
                deferredVehicles.push({
                  vehicleData: {
                    vehicleId: vehicle.vehicleId,
                    plate: vehicle.plate,
                    operation: vehicle.operation,
                    color: vehicle.color,
                    operationRouteId: vehicle.operationRouteId,
                  },
                  originalDate: dateStr,
                });
              }
            });
          }

          const capacityData = capacityInfo[dateStr] || {};

          // Actualizar capacidad para días con eventos
          if (hasEvents) {
            return {
              ...day,
              vehicles: existingVehicles,
              events: events,
              capacity: {
                ...capacityData,
                scheduledCount: 0,
                scheduled: 0,
                status: "blocked_by_event",
              },
              hasCapacityIssue: false,
              isBlockedByEvent: true,
              blockedMessage: `Día bloqueado por evento: ${
                events[0]?.title || "Evento"
              }`,
            };
          }

          return {
            ...day,
            vehicles: existingVehicles,
            events: existingData.events || [],
            capacity: capacityData,
            hasCapacityIssue:
              capacityData.status === "over_capacity" ||
              capacityData.status === "under_capacity",
          };
        }),
      }));
    },
    [selectedRouteId, routes, showNotification]
  );

  const generateWeeksData = useCallback((year, month) => {
    if (isNaN(year) || isNaN(month)) {
      console.error("Valores inválidos para generateWeeksData:", year, month);
      return [];
    }

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    const totalWeeks = Math.ceil((daysInMonth + startDayOfWeek) / 7);
    const weeks = [];
    const weekDays = [
      "Domingo",
      "Lunes",
      "Martes",
      "Miércoles",
      "Jueves",
      "Viernes",
      "Sábado",
    ];

    let dayCount = 1;

    for (let weekNum = 0; weekNum < totalWeeks; weekNum++) {
      const weekData = {
        weekNumber: weekNum + 1,
        days: [],
      };

      for (let day = 0; day < 7; day++) {
        if ((weekNum === 0 && day < startDayOfWeek) || dayCount > daysInMonth) {
          weekData.days.push({
            dayName: weekDays[day],
            date: null,
            formattedDate: "",
            vehicles: [],
            events: [],
            capacity: null,
          });
        } else {
          const currentDate = new Date(year, month, dayCount);
          const isoDate = `${year}-${String(month + 1).padStart(
            2,
            "0"
          )}-${String(dayCount).padStart(2, "0")}`;
          const formattedDate = `${String(dayCount).padStart(2, "0")}/${String(
            month + 1
          ).padStart(2, "0")}/${year}`;

          weekData.days.push({
            dayName: weekDays[day],
            date: isoDate,
            dateObj: currentDate,
            formattedDate: formattedDate,
            vehicles: [],
            events: [],
            capacity: null,
          });

          dayCount++;
        }
      }

      weeks.push(weekData);
    }

    return weeks;
  }, []);

  // ACTUALIZADO: Generar datos de semanas basándose en currentDate
  const baseWeeksData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return generateWeeksData(year, month);
  }, [currentDate, generateWeeksData]);

  // ACTUALIZADO: Efecto principal para actualizar calendario con datos del servidor
  useEffect(() => {
    if (!scheduleData?.data || !baseWeeksData.length) {
      setWeeksData(baseWeeksData);
      return;
    }

    const scheduledDays = scheduleData.data.reduce((acc, dayData) => {
      if (dayData && dayData.date) {
        acc[dayData.date] = dayData;
      }
      return acc;
    }, {});

    // Clonar profundamente los datos base
    let updatedWeeksData = JSON.parse(JSON.stringify(baseWeeksData));

    updatedWeeksData = updatedWeeksData.map((week) => ({
      ...week,
      days: week.days.map((day) => {
        if (!day.date) return day;

        const scheduledData = scheduledDays[day.date];

        // Buscar información de capacidad con filtro de ruta
        let capacityInfo = null;

        if (capacityStatus?.data && Array.isArray(capacityStatus.data)) {
          capacityInfo = capacityStatus.data.find((c) => {
            let cDate;
            if (typeof c.date === "string") {
              if (c.date.includes("T")) {
                cDate = c.date.split("T")[0];
              } else {
                cDate = c.date;
              }
            } else if (c.date instanceof Date) {
              cDate = c.date.toISOString().split("T")[0];
            }

            const dateMatches = cDate === day.date;

            if (!selectedRouteId) {
              return (
                dateMatches &&
                (!c.operationRouteId || c.operationRouteId === null)
              );
            }

            return dateMatches && c.operationRouteId === selectedRouteId;
          });
        }

        const dayVehicles = scheduledData?.vehicles || [];

        const routeFilteredVehicles = selectedRouteId
          ? dayVehicles.filter(
              (v) =>
                v.operationRouteId === selectedRouteId ||
                v.route?.id === selectedRouteId
            )
          : dayVehicles.filter((v) => !v.operationRouteId);

        const realActiveVehicles = routeFilteredVehicles.filter(
          (v) => !v.is_cancelled && !v.isPredicted
        );
        const realCancelledVehicles = routeFilteredVehicles.filter(
          (v) => v.is_cancelled
        );
        const realVehicleCount = realActiveVehicles.length;
        const realCancelledCount = realCancelledVehicles.length;

        let capacityData = null;
        if (capacityInfo) {
          const plannedCapacity = capacityInfo.plannedCapacity || 0;
          const realDifference = realVehicleCount - plannedCapacity;

          let realStatus = "no_capacity_set";
          if (plannedCapacity > 0) {
            if (realDifference > 0) {
              realStatus = "over_capacity";
            } else if (realDifference < 0) {
              realStatus = "under_capacity";
            } else {
              realStatus = "at_capacity";
            }
          }

          capacityData = {
            planned: plannedCapacity,
            plannedCapacity: plannedCapacity,
            scheduled: realVehicleCount,
            scheduledCount: realVehicleCount,
            cancelled: realCancelledCount,
            cancelledCount: realCancelledCount,
            status: realStatus,
            difference: realDifference,
            isOverCapacity: realStatus === "over_capacity",
            isUnderCapacity: realStatus === "under_capacity",
            operationRouteId: capacityInfo.operationRouteId,
            originalCapacityData: capacityInfo,
            hasRouteFilter: !!selectedRouteId,
          };
        }

        return {
          ...day,
          vehicles: routeFilteredVehicles,
          events: scheduledData?.events || [],
          capacity: capacityData,
          hasCapacityIssue: capacityData
            ? capacityData.status === "over_capacity" ||
              capacityData.status === "under_capacity"
            : false,
        };
      }),
    }));

    if (showPredictions) {
      const capacityByDate = {};
      if (capacityStatus?.data && Array.isArray(capacityStatus.data)) {
        capacityStatus.data.forEach((c) => {
          let dateKey;
          if (typeof c.date === "string") {
            if (c.date.includes("T")) {
              dateKey = c.date.split("T")[0];
            } else {
              dateKey = c.date;
            }
          } else if (c.date instanceof Date) {
            dateKey = c.date.toISOString().split("T")[0];
          }

          if (dateKey) {
            if (
              !selectedRouteId &&
              (!c.operationRouteId || c.operationRouteId === null)
            ) {
              capacityByDate[dateKey] = c;
            } else if (
              selectedRouteId &&
              c.operationRouteId === selectedRouteId
            ) {
              capacityByDate[dateKey] = c;
            }
          }
        });
      }

      updatedWeeksData = generateSimplePredictions(
        scheduledDays,
        updatedWeeksData,
        capacityByDate
      );
    }

    setWeeksData(updatedWeeksData);
  }, [
    scheduleData,
    baseWeeksData,
    showPredictions,
    capacityStatus,
    selectedRouteId,
    generateSimplePredictions,
    routes,
  ]);

  // ACTUALIZADO: Refrescar datos cuando cambia la ruta seleccionada
  useEffect(() => {
    if (currentOperationId && dateRange.startDate && dateRange.endDate) {
      refetchSchedule();
    }
  }, [
    selectedRouteId,
    refetchSchedule,
    currentOperationId,
    dateRange.startDate,
    dateRange.endDate,
  ]);

  const handleTogglePredictions = () => {
    setShowPredictions((prev) => !prev);
  };

  // NUEVO: Función mejorada para cambio de mes
  const handleMonthChange = useCallback(
    (monthIndex) => {
      if (monthIndex < 0 || monthIndex > 11) {
        console.error("Índice de mes inválido:", monthIndex);
        return;
      }

      // Prevenir cambio si ya estamos en ese mes
      if (monthIndex === currentMonthYear.month) {
        return;
      }

      // Construir nueva fecha preservando el día si es posible
      const currentDay = new Date(selectedDate).getDate();
      const daysInNewMonth = new Date(
        currentMonthYear.year,
        monthIndex + 1,
        0
      ).getDate();
      const safeDay = Math.min(currentDay, daysInNewMonth);

      const newDate = new Date(currentMonthYear.year, monthIndex, safeDay);
      const formattedDate = newDate.toISOString().split("T")[0];
      handleDateChange(formattedDate);
    },
    [currentMonthYear, selectedDate, handleDateChange]
  );

  const handleVehiclesClick = () => {
    setVehicleDrawerOpen(true);
  };

  const handleEventsClick = () => {
    setEventsDrawerOpen(true);
  };

  const handleCapacityClick = () => {
    setCapacityDrawerOpen(true);
  };

  const handleRoutesClick = () => {
    setRouteDrawerOpen(true);
  };

  const handleRouteChange = useCallback(
    (routeId) => {
      setSelectedRouteId(routeId);
      refetchSchedule();
    },
    [refetchSchedule]
  );

  const handleRouteSelect = useCallback(
    (routeId, routeInfo) => {
      setSelectedRouteId(routeId);
      showNotification({
        message: `Ruta seleccionada: ${routeInfo.route_name}`,
        severity: "info",
      });
    },
    [showNotification]
  );
  const handleCancelVehicle = useCallback((scheduleId, vehicleInfo) => {
  try {
    // Normalizar fechas para comparación precisa
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    // Normalizar fecha del vehículo (puede venir en diferentes formatos)
    let vehicleDateStr;
    if (typeof vehicleInfo.date === 'string') {
      // Si es string, tomar solo la parte de fecha (YYYY-MM-DD)
      vehicleDateStr = vehicleInfo.date.split('T')[0];
    } else if (vehicleInfo.date instanceof Date) {
      // Si es objeto Date, convertir a string YYYY-MM-DD
      const vDate = vehicleInfo.date;
      vehicleDateStr = `${vDate.getFullYear()}-${String(vDate.getMonth() + 1).padStart(2, '0')}-${String(vDate.getDate()).padStart(2, '0')}`;
    } else {
      console.error('Formato de fecha no reconocido:', vehicleInfo.date);
      setSnackbar({
        open: true,
        message: "Error: formato de fecha inválido",
        severity: "error",
      });
      return;
    }

    console.log('Comparación de fechas:', {
      todayStr,
      vehicleDateStr,
      areEqual: todayStr === vehicleDateStr
    });

    // Verificar si es el día actual
    if (vehicleDateStr === todayStr) {
      // Es el día actual, permitir cancelación
      setCancelModal({
        open: true,
        scheduleId,
        vehicleInfo: {
          ...vehicleInfo,
          normalizedDate: vehicleDateStr
        },
      });
    } else if (vehicleDateStr < todayStr) {
      // Es un día pasado
      setSnackbar({
        open: true,
        message: "No se pueden cancelar vehículos de días pasados",
        severity: "warning",
      });
    } else {
      // Es un día futuro (esto no debería pasar si el botón está deshabilitado correctamente)
      setSnackbar({
        open: true,
        message: "Solo se pueden cancelar vehículos del día actual",
        severity: "warning",
      });
    }
  } catch (error) {
    console.error('Error en handleCancelVehicle:', error);
    setSnackbar({
      open: true,
      message: "Error al procesar la cancelación",
      severity: "error",
    });
  }
}, []);
  const handleExportDay = useCallback(async (date, vehicles, routeId, routeName) => {
  try {
    // Mostrar notificación de inicio
    showNotification({
      message: "Generando archivo Excel...",
      severity: "info",
    });

    // Preparar los datos para enviar
    const exportData = {
      date,
      vehicles: vehicles.map(v => ({
        vehicleId: v.vehicleId,
        plate: v.plate,
        position: v.position,
        operationRouteId: v.operationRouteId
      })),
      routeId: routeId || null,
      routeName: routeName || 'Sin Ruta',
      operationId: currentOperationId,
      operationName: operationInfo?.name || 'Operación'
    };

    // Llamar al servicio de exportación
    const response = await operationService.exportDaySchedule(
      currentOperationId,
      date,
      exportData
    );

    // Si la respuesta es exitosa, descargar el archivo
    if (response && response.data) {
      // Crear un blob con los datos del Excel
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      // Crear URL temporal y descargar
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Formatear nombre del archivo
      const [year, month, day] = date.split('-');
      const fileName = `Programacion_${operationInfo?.name || 'Operacion'}_${day}-${month}-${year}.xlsx`;
      
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      // Limpiar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showNotification({
        message: "Archivo Excel descargado exitosamente",
        severity: "success",
      });
    }
  } catch (error) {
    console.error("Error al exportar programación:", error);
    showNotification({
      message: error.message || "Error al generar el archivo Excel",
      severity: "error",
    });
  }
}, [currentOperationId, operationInfo, operationService, showNotification]);

  const handleConfirmCancellation = async (cancellationData) => {
    try {
      setIsLoading(true);

      await cancelVehicle({
        scheduleId: cancelModal.scheduleId,
        ...cancellationData,
      });

      setSnackbar({
        open: true,
        message: "Vehículo cancelado exitosamente",
        severity: "success",
      });

      refetchSchedule();
      setCancelModal({ open: false, scheduleId: null, vehicleInfo: null });
    } catch (error) {
      console.error("Error cancelando vehículo:", error);
      setSnackbar({
        open: true,
        message: error.message || "Error al cancelar vehículo",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderCapacityIndicator = (capacity) => {
    if (!capacity) return null;

    const getStatusIcon = () => {
      switch (capacity.status) {
        case "over_capacity":
          return <ErrorIcon fontSize="small" />;
        case "under_capacity":
          return <WarningIcon fontSize="small" />;
        case "at_capacity":
          return <CheckCircleIcon fontSize="small" />;
        default:
          return null;
      }
    };

    const getStatusColor = () => {
      switch (capacity.status) {
        case "over_capacity":
          return "error";
        case "under_capacity":
          return "warning";
        case "at_capacity":
          return "success";
        default:
          return "default";
      }
    };

    return (
      <Tooltip
        title={`Capacidad: ${capacity.scheduledCount}/${capacity.plannedCapacity}`}
      >
        <Chip
          icon={getStatusIcon()}
          label={`${capacity.scheduledCount}/${capacity.plannedCapacity}`}
          size="small"
          color={getStatusColor()}
          className={`GM__${theme}-capacity-indicator`}
        />
      </Tooltip>
    );
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    try {
      setIsLoading(true);

      if (destination.droppableId === source.droppableId) {
        let targetDay = null;

        for (const week of weeksData) {
          const dayIndex = week.days.findIndex(
            (d) => d.date && d.date === destination.droppableId // ACTUALIZADO: Comparación directa sin conversión
          );

          if (dayIndex !== -1) {
            targetDay = week.days[dayIndex];
            break;
          }
        }

        if (!targetDay || !targetDay.vehicles) return;

        const vehicleItems = [...targetDay.vehicles];
        const [movedVehicle] = vehicleItems.splice(source.index, 1);
        vehicleItems.splice(destination.index, 0, movedVehicle);

        const updatedVehicles = vehicleItems.map((vehicle, index) => ({
          id: vehicle.id,
          position: index,
        }));

        await reorderVehicles({
          date: destination.droppableId,
          vehicles: updatedVehicles,
        });

        // ACTUALIZADO: Actualización inmediata del estado local
        setWeeksData((prevWeeksData) => {
          return prevWeeksData.map((week) => ({
            ...week,
            days: week.days.map((day) => {
              if (day.date && day.date === destination.droppableId) {
                return {
                  ...day,
                  vehicles: vehicleItems.map((vehicle, index) => ({
                    ...vehicle,
                    position: index,
                  })),
                };
              }
              return day;
            }),
          }));
        });
      } else {
        let sourceDay = null;
        let destinationDay = null;

        for (const week of weeksData) {
          for (const day of week.days) {
            if (day.date && day.date === source.droppableId) {
              sourceDay = day;
            }
            if (day.date && day.date === destination.droppableId) {
              destinationDay = day;
            }
          }
        }

        if (!sourceDay || !destinationDay) return;

        const movedVehicle = sourceDay.vehicles[source.index];

        await moveVehicle({
          scheduleId: movedVehicle.id,
          newDate: destination.droppableId,
          newPosition: destination.index,
        });

        refetchSchedule();
      }
    } catch (error) {
      console.error("Error al mover vehículo:", error);
      setSnackbar({
        open: true,
        message:
          "Error al mover vehículo: " + (error.message || "Error desconocido"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteVehicle = async (scheduleId) => {
    if (
      window.confirm(
        "¿Estás seguro de eliminar este vehículo de la programación?"
      )
    ) {
      try {
        setIsLoading(true);
        await removeVehicle(scheduleId);
        setSnackbar({
          open: true,
          message: "Vehículo eliminado de la programación",
          severity: "success",
        });
        refetchSchedule();
      } catch (error) {
        console.error("Error al eliminar vehículo de programación:", error);
        setSnackbar({
          open: true,
          message:
            "Error al eliminar vehículo: " +
            (error.message || "Error desconocido"),
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleAddVehicleClick = (date) => {
    setAddVehicleModal({
      open: true,
      date,
    });
  };

  const handleAddVehicleClose = () => {
    setAddVehicleModal({
      open: false,
      date: null,
    });
  };

  const handleAddVehicles = async (vehicleIds) => {
    if (!vehicleIds.length) return;

    if (routes.length > 1 && !selectedRouteId) {
      setSnackbar({
        open: true,
        message: "Por favor selecciona una ruta antes de agregar vehículos",
        severity: "warning",
      });
      return;
    }

    try {
      setIsLoading(true);

      const promises = vehicleIds.map((vehicleId) =>
        addVehicle({
          vehicleId,
          date: addVehicleModal.date,
          operationRouteId: selectedRouteId,
        })
      );

      await Promise.all(promises);

      setSnackbar({
        open: true,
        message: `${vehicleIds.length} vehículo(s) añadido(s) correctamente`,
        severity: "success",
      });

      refetchSchedule();
    } catch (error) {
      console.error("Error al añadir vehículos:", error);
      setSnackbar({
        open: true,
        message:
          "Error al añadir vehículos: " +
          (error.message || "Error desconocido"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      handleAddVehicleClose();
    }
  };

  const handleEventClick = (event) => {
    setEventsDrawerOpen(true);
  };

  const handleVehiclesChange = () => {
    refetchSchedule();
  };
  

  const isPageLoading =
    isLoading || isLoadingSchedule || isLoadingOperation || isLoadingCapacity;
  const combinedError = error || scheduleError;

  if (isPageLoading) return <LoadingAnimation />;
  if (combinedError)
    return (
      <div className={`GM__${theme}-error-message`}>
        {typeof combinedError === "object"
          ? combinedError.message
          : combinedError}
      </div>
    );

  return (
    <div className={`GM__${theme}-scheduler-calendar`}>
      <SchedulerHeader
        theme={theme}
        operationName={operationInfo?.name}
        onVehiclesClick={handleVehiclesClick}
        onEventsClick={handleEventsClick}
        onCapacityClick={handleCapacityClick}
        onRoutesClick={handleRoutesClick}
        showPredictions={showPredictions}
        onTogglePredictions={handleTogglePredictions}
        capacityStatus={capacityStatus}
        routes={routes}
        selectedRouteId={selectedRouteId}
        onRouteChange={handleRouteChange}
        isLoadingRoutes={isLoadingRoutes}
      />

      <div className={`GM__${theme}-scheduler-controls`}>
        <MonthSelector
          theme={theme}
          currentMonth={currentMonthYear.month}
          onMonthChange={handleMonthChange}
          year={currentMonthYear.year}
        />
      </div>

      <div className={`GM__${theme}-calendar-container`}>
        {weeksData.map((week) => (
          <WeekCalendar
            key={`week-${week.weekNumber}`}
            theme={theme}
            weekNumber={week.weekNumber}
            days={week.days}
            onDragEnd={handleDragEnd}
            onDeleteVehicle={handleDeleteVehicle}
            onCancelVehicle={handleCancelVehicle}
            onEventClick={handleEventClick}
            isPastDate={isPastDate}
            isToday={isToday}
            onVehicleAdd={handleAddVehicleClick}
            renderCapacityIndicator={renderCapacityIndicator}
            routes={routes}
            selectedRouteId={selectedRouteId}
            operationId={currentOperationId}
            onExportDay={handleExportDay}
          />
        ))}
      </div>

      <VehicleDrawer
        open={vehicleDrawerOpen}
        onClose={() => setVehicleDrawerOpen(false)}
        operationId={currentOperationId}
        operationName={operationInfo?.name}
        onVehiclesChange={handleVehiclesChange}
        selectedRouteId={selectedRouteId}
      />

      <EventsDrawer
        open={eventsDrawerOpen}
        onClose={() => setEventsDrawerOpen(false)}
        operationId={currentOperationId}
        operationName={operationInfo?.name}
        selectedRouteId={selectedRouteId}
      />

      <CapacityDrawer
        open={capacityDrawerOpen}
        onClose={() => setCapacityDrawerOpen(false)}
        operationId={currentOperationId}
        operationName={operationInfo?.name}
        currentYear={currentDate.getFullYear()}
        currentMonth={currentDate.getMonth() + 1}
        selectedRouteId={selectedRouteId}
      />

      <RouteDrawer
        open={routeDrawerOpen}
        onClose={() => setRouteDrawerOpen(false)}
        operationId={currentOperationId}
        operationName={operationInfo?.name}
        onRouteSelect={handleRouteSelect}
        selectedRouteId={selectedRouteId}
      />

      <AddVehicleModal
        open={addVehicleModal.open}
        onClose={handleAddVehicleClose}
        operationId={currentOperationId}
        selectedDate={addVehicleModal.date}
        onVehicleAdd={handleAddVehicles}
        theme={theme}
        selectedRouteId={selectedRouteId}
        routeName={routes.find((r) => r.id === selectedRouteId)?.route_name}
      />

      <CancelVehicleModal
        open={cancelModal.open}
        onClose={() =>
          setCancelModal({ open: false, scheduleId: null, vehicleInfo: null })
        }
        vehicleInfo={cancelModal.vehicleInfo}
        operationId={currentOperationId}
        onConfirm={handleConfirmCancellation}
        selectedRouteId={selectedRouteId}
        theme={theme}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

SchedulerCalendar.propTypes = {
  operationId: PropTypes.string,
};

export default SchedulerCalendar;
